import { useRef, useEffect } from "react";

export default function OtpInput({ value, onChange, length = 6, disabled = false }) {
  const inputsRef = useRef([]);

  const digits = value.padEnd(length, " ").split("").slice(0, length);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const focusInput = (index) => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange = (index, char) => {
    if (!/^\d?$/.test(char)) return;

    const next = digits.map((d, i) => (i === index ? char : d.trim())).join("").slice(0, length);
    onChange(next.replace(/\s/g, ""));

    if (char && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]?.trim()) {
        handleChange(index, "");
      } else if (index > 0) {
        focusInput(index - 1);
        handleChange(index - 1, "");
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted) {
      onChange(pasted);
      focusInput(Math.min(pasted.length, length - 1));
    }
  };

  return (
    <div className="flex justify-between gap-1.5 sm:gap-2">
      {digits.map((digit, index) => {
        const hasValue = Boolean(digit.trim());

        return (
          <div key={index} className="group relative w-full max-w-[44px]">
            <input
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              disabled={disabled}
              value={digit.trim()}
              onChange={(e) => handleChange(index, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className="
                h-11 w-full border-0 bg-primary/[0.03] text-center
                text-base font-bold text-primary shadow-none outline-none ring-0
                transition-colors duration-300
                focus:bg-accent/5 focus:outline-none focus:ring-0 focus-visible:outline-none
                disabled:cursor-not-allowed disabled:opacity-50
              "
              aria-label={`Digit ${index + 1} of ${length}`}
            />
            <span
              className={`pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] ${
                hasValue ? "bg-accent/40" : "bg-primary/15"
              }`}
              aria-hidden="true"
            />
            <span
              className={`pointer-events-none absolute bottom-0 left-0 h-[2px] w-full origin-left bg-accent transition-transform duration-300 ease-out ${
                hasValue ? "scale-x-100" : "scale-x-0 group-focus-within:scale-x-100"
              }`}
              aria-hidden="true"
            />
          </div>
        );
      })}
    </div>
  );
}
